import Button from '@raydiant/raydial/components/Button';
import Dialog, {
  DialogBody,
  DialogHeader,
  DialogHeading,
  DialogSubheading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import Spinner from '@raydiant/raydial/components/Spinner';
import history from '../../history';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import useConnections from '../../hooks/useConnections';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import * as paths from '../../routes/paths';
import { Connection } from '@raydiant/api-client-js';
import ProductHubCard from '../../components/ProductHubCard';
import { CheckCircle, CircleNodes } from '@raydiant/raydial/components/Icons';
import Text from '@raydiant/raydial/components/Text';
import TextField from '@raydiant/raydial/components/TextField';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import config from '../../config';
import Link from '@raydiant/raydial/components/Link';
import { useToastState } from '@raydiant/raydial/components/Toast';
import useDeviceTerminal from '../../hooks/useDeviceTerminal';
import { useTranslation } from 'react-i18next';

const { supportAppUrl, publicAssetUrl } = config;

interface SquareLocation {
  id: string;
  name: string;
  address: object;
  status: string;
}

interface SquareConnectionSettings {
  authId: string;
}

interface NewPaymentTerminalProps {
  isOpen: boolean;
  deviceId: string;
  deviceName: string;
}

const Step1 = ({
  onSubmit,
  onCancel,
  connectorName,
}: {
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  connectorName: string;
}) => {
  return (
    <>
      <DialogHeader>
        <DialogHeading>Add a payment terminal</DialogHeading>
        <DialogSubheading>Choose a payment provider</DialogSubheading>
      </DialogHeader>

      <DialogBody
        as="form"
        onSubmit={onSubmit}
        className="grid gap-4 mb-8"
        id="step-1-form"
      >
        <div className="px-4">
          <ProductHubCard
            imgSrc={`${publicAssetUrl}/connectors/square.svg`}
            title="Square"
            description="Connect with a Square terminal"
            selected={connectorName === 'square'}
            // We need to add onPress here in order for the correct styles to be applied to the component
            // Eventually a proper handler that sets the payment provider will be added when freedom pay is added
            onPress={() => {}}
          />
        </div>
      </DialogBody>

      <footer className="flex flex-wrap gap-6 pb-4 px-4">
        <Button
          size="large"
          variant="secondary"
          className="grow"
          onPress={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="primary"
          className="grow"
          type="submit"
          form="step-1-form"
        >
          Next
        </Button>
      </footer>
    </>
  );
};

const Step2 = ({
  onSubmit,
  onCancel,
  isLoading,
  connections,
  connectionId,
  onConnectionSelect,
}: {
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  isLoading: boolean;
  connections: Connection[];
  connectionId: string;
  onConnectionSelect: (connectionId: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DialogHeader>
        <DialogHeading>Select Square integration</DialogHeading>
        <DialogSubheading>Choose a payment provider</DialogSubheading>
      </DialogHeader>

      <DialogBody
        as="form"
        onSubmit={onSubmit}
        className="grid gap-4"
        id="step-2-form"
      >
        <LoadingModal isOpen={isLoading} />

        {connections.length > 0 &&
          connections.map((connection) => (
            <ProductHubCard
              key={connection.id}
              imgSrc={connection.connector.thumbnailUrl}
              title={connection.name}
              description={connection.connector.displayName}
              onPress={() => onConnectionSelect(connection.id)}
              selected={connectionId === connection.id}
            />
          ))}
        {!connections ||
          (connections.length === 0 && (
            <ProductHubCard
              title="Add an integration"
              icon={<CircleNodes className="size-[30px] text-gray-500" />}
              description={`Connect with a ${t('companyName')} partner`}
              rightAction={
                <Button
                  onPress={() => {
                    history.push(paths.createIntegrations());
                  }}
                >
                  Add
                </Button>
              }
            />
          ))}
      </DialogBody>

      <footer className="flex flex-wrap gap-6 pb-4 px-4 mt-8">
        <Button
          size="large"
          variant="secondary"
          className="grow"
          onPress={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="primary"
          className="grow"
          type="submit"
          form="step-2-form"
          isDisabled={!connectionId}
        >
          Next
        </Button>
      </footer>
    </>
  );
};

const Step3 = ({
  onSubmit,
  onCancel,
  connectionName,
  authId,
  onLocationSelect,
  selectedLocation,
  onTerminalNicknameChange,
  isSaving,
  terminalNickname,
}: {
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  connectionName: string;
  authId: string;
  onLocationSelect: (locationId: string) => void;
  selectedLocation: string;
  terminalNickname: string;
  onTerminalNicknameChange: (terminalNickname: string) => void;
  isSaving?: boolean;
}) => {
  let [locations, setLocations] = useState<SquareLocation[]>([]);
  const [isFetchingLocations, setIsFetchingLocations] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const squareLocationsEndpointUrl = `${config.squareConnectorApiUrl}/product-catalog/locations?auth_id=${authId}`;
        const response = await fetch(squareLocationsEndpointUrl);
        const data = await response.json();

        setLocations(data.locations);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setIsFetchingLocations(false);
      }
    };

    fetchLocations();
  }, [authId]);

  const isValid = selectedLocation.length > 0 && terminalNickname.length > 0;

  return (
    <>
      <DialogHeader>
        <DialogHeading>Pair Kiosk with Square Terminal</DialogHeading>
        <DialogSubheading>
          Pairing terminal with {connectionName}
        </DialogSubheading>
      </DialogHeader>

      <DialogBody
        as="form"
        onSubmit={onSubmit}
        className="grid gap-4 pt-4"
        id="step-3-form"
      >
        <LoadingModal isOpen={isSaving || isFetchingLocations} />
        <div className="px-4">
          <Text variant="smallSubheader" as="p" className="mb-1">
            Terminal details
          </Text>
          <Text as="p" className="text-gray-600">
            Enter details for the terminal
          </Text>
        </div>
        <TextField
          aria-label="Terminal Nickname"
          value={terminalNickname}
          onChange={onTerminalNicknameChange}
        />
        <Select
          label="Square Terminal Location"
          placeholder="Select a location"
          selectedKey={selectedLocation}
          onSelectionChange={(key) => {
            onLocationSelect(key as string);
          }}
          isDisabled={isFetchingLocations}
        >
          {locations.length > 0 ? (
            locations.map((l) => (
              <SelectItem key={l.id} id={l.id}>
                {l.name}
              </SelectItem>
            ))
          ) : (
            <SelectItem>
              No locations available — Add a Square location in your{' '}
              <Link
                target="_blank"
                href="//app.squareup.com/dashboard/locations"
              >
                Square Dashboard
              </Link>
            </SelectItem>
          )}
        </Select>
      </DialogBody>

      <footer className="flex flex-wrap gap-6 pb-4 px-4 mt-8">
        <Button
          size="large"
          variant="secondary"
          className="grow"
          onPress={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="primary"
          className="grow"
          type="submit"
          form="step-3-form"
          isDisabled={!isValid}
        >
          Next
        </Button>
      </footer>
    </>
  );
};

const Step4 = ({
  connectionName,
  onCancel,
  deviceCode,
  onComplete,
  terminalNickname,
  deviceId,
}: {
  connectionName: string;
  onCancel: () => void;
  onComplete: () => void;
  deviceCode: string;
  terminalNickname: string;
  deviceId: string;
}) => {
  const toastState = useToastState();
  const refetchInterval = 5000;
  const deviceTerminalQuery = useDeviceTerminal(deviceId, { refetchInterval });
  const prevDeviceTerminalQueryRef = useRef(deviceTerminalQuery);

  useEffect(() => {
    if (
      prevDeviceTerminalQueryRef.current.isFetching &&
      deviceTerminalQuery.isSuccess
    ) {
      toastState.add({
        title: 'Payment Terminal Paired',
        description: 'Square terminal added successfully.',
      });
    }
    prevDeviceTerminalQueryRef.current = deviceTerminalQuery;
  }, [deviceTerminalQuery, deviceTerminalQuery.isSuccess, toastState]);

  // The API will send a 404 when the terminal is not paired
  // Therefore we use both the isFetching and isError flags to determine when
  // the api is polling
  const isPolling =
    deviceTerminalQuery.isFetching || deviceTerminalQuery.isError;

  return (
    <>
      <DialogHeader>
        <DialogHeading>Pair Kiosk with Square Terminal</DialogHeading>
        <DialogSubheading>
          Pairing terminal with {connectionName}
        </DialogSubheading>
      </DialogHeader>

      <DialogBody className="grid gap-4 pt-4" id="step-3-form">
        <div className="px-4">
          {isPolling && (
            <>
              <div className="mb-4">
                <Text variant="smallSubheader" as="p" className="mb-1">
                  Use this code to sign in on your Square Terminal
                </Text>
                <Text as="p" className="text-gray-600">
                  Select “Use a Device Code” after “Sign In”.{' '}
                  <Link
                    target="_blank"
                    href={`${supportAppUrl}/s/article/Square-POS-Connector-Setup-Instructions`}
                  >
                    Learn More
                  </Link>
                </Text>
              </div>

              <div className="mb-8 flex justify-center relative">
                <div className="left-8 absolute top-1/2 -translate-y-1/2">
                  <Spinner className="size-9 text-gray-400" />
                </div>
                <Text className="text-blue-600 text-4xl">{deviceCode}</Text>
              </div>
            </>
          )}

          {deviceTerminalQuery.isSuccess && (
            <>
              <div className="mb-4">
                <Text variant="smallSubheader" as="p" className="mb-1">
                  Connected!
                </Text>
                <Text as="p" className="text-gray-600">
                  {terminalNickname} was paired Successfully
                </Text>
              </div>

              <div className="mb-8 flex justify-center relative">
                <CheckCircle className="size-16 text-green-600" />
              </div>
            </>
          )}
        </div>
      </DialogBody>

      <footer className="flex flex-wrap gap-6 pb-4 px-4 mt-8">
        {isPolling && (
          <Button
            size="large"
            variant="secondary"
            className="grow"
            onPress={onCancel}
          >
            Cancel
          </Button>
        )}

        {deviceTerminalQuery.isSuccess && (
          <Button
            size="large"
            variant="primary"
            className="grow"
            onPress={onComplete}
          >
            Done
          </Button>
        )}
      </footer>
    </>
  );
};

const NewPaymentTerminal = ({
  isOpen,
  deviceId,
  deviceName,
}: NewPaymentTerminalProps) => {
  const connectionsQuery = useConnections();
  const defaultFormState = useMemo(
    () => ({
      connectorName: 'square',
      connectionId: '',
      locationId: '',
      terminalNickname: deviceName,
    }),
    [deviceName],
  );
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultFormState);
  const [isGeneratingPairingCode, setIsGeneratingPairingCode] = useState(false);
  const [deviceCode, setDeviceCode] = useState('');

  const incrementStep = (e: FormEvent) => {
    e.preventDefault();
    setStep(step + 1);
  };

  const onStep3Submit = async (e: FormEvent) => {
    e.preventDefault();
    const requestBody = {
      connection_id: form.connectionId,
      device_id: deviceId,
      terminal_name: form.terminalNickname,
      terminal_location_id: form.locationId,
    };

    try {
      setIsGeneratingPairingCode(true);
      const squareDeviceCodeEndpointUrl = `${config.squareConnectorApiUrl}/product-catalog/device-codes?auth_id=${selectedConnectionSettings.authId}`;
      const response = await fetch(squareDeviceCodeEndpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      setDeviceCode(data.code);
    } catch (error) {
      console.error(error);
    } finally {
      setIsGeneratingPairingCode(false);
      setStep(step + 1);
    }
  };

  const handleCancel = () => {
    history.push(paths.screens.pattern);
  };

  const handleOnComplete = () => {
    // Redirect to screens page and re-fetch device data
    window.location.href = paths.screens.pattern;
  };

  if (!deviceId) {
    return null;
  }

  const { data: connections } = connectionsQuery.data || { data: [] };

  const squareConnections = connections.filter(
    (c) => c.connector.name === 'square',
  );

  const selectedConnection = squareConnections.find(
    (connection) => connection.id === form.connectionId,
  );

  const selectedConnectionSettings =
    selectedConnection?.settings as SquareConnectionSettings;

  return (
    <Modal isOpen={isOpen}>
      <Dialog>
        {step === 1 && (
          <Step1
            onSubmit={incrementStep}
            onCancel={handleCancel}
            connectorName={form.connectorName}
          />
        )}

        {step === 2 && (
          <Step2
            onSubmit={incrementStep}
            onCancel={handleCancel}
            isLoading={connectionsQuery.isLoading}
            connections={squareConnections}
            connectionId={form.connectionId}
            onConnectionSelect={(connectionId: string) => {
              setForm({ ...form, connectionId });
            }}
          />
        )}

        {step === 3 && (
          <Step3
            onSubmit={onStep3Submit}
            onCancel={handleCancel}
            connectionName={selectedConnection?.name ?? ''}
            authId={selectedConnectionSettings.authId}
            selectedLocation={form.locationId}
            onLocationSelect={(locationId: string) => {
              setForm({ ...form, locationId });
            }}
            terminalNickname={form.terminalNickname}
            onTerminalNicknameChange={(terminalNickname: string) => {
              setForm({ ...form, terminalNickname });
            }}
            isSaving={isGeneratingPairingCode}
          />
        )}

        {step === 4 && (
          <Step4
            onCancel={handleCancel}
            connectionName={selectedConnection?.name ?? ''}
            deviceCode={deviceCode}
            onComplete={handleOnComplete}
            terminalNickname={form.terminalNickname}
            deviceId={deviceId}
          />
        )}
      </Dialog>
    </Modal>
  );
};

export default NewPaymentTerminal;
